/**
 * The run form's ITEMS and SECTIONS, as pure functions (MAR-948 split of `WorkflowRunForm.vue`).
 *
 * Three kinds of item, one renderer each. `doc` items are grouped into a leading Documents section;
 * `field` and `param` fill the remaining sections, split by `inputGroups` when the definition
 * declares them. Everything here is a function of the definition and the current values — the
 * reactive state (what is collapsed, whether batch is on) lives in `app/composables/workflow-run/`.
 *
 * ── ONE LAYOUT (MAR-134) ──
 * There used to be three: a numbered WIZARD that collapsed each step once filled, titled SECTION
 * cards, and both stacked. Everything is sections now. Sections don't hide what you already answered
 * and don't auto-advance the viewport out from under you. Required fields are never collapsed.
 */
import type { Ref } from 'vue'
import { documentTypeById } from '#shared/documentTypes'
import type { ContractType } from '#shared/contractTypes'
import { isIdentifierProperty } from '#shared/entities/vocabulary'
import { optionLabelFor } from '#shared/workflows/fieldOptions'
import type { BatchColumn } from '~/types/workflowBatch'
import type { WorkflowRequestField } from '~/types/web-chat'
import { isBlankValue } from './workflowFields'

/**
 * What the run form reads off a loaded `useWorkflowBuilder()` — declared rather than `any`, so a
 * builder that stops exposing one of these fails to compile instead of rendering an empty form.
 */
export interface RunFormBuilder {
  loaded: Readonly<Ref<boolean>>
  name: Readonly<Ref<string>>
  description: Readonly<Ref<string | null | undefined>>
  inputs: Readonly<Ref<readonly any[]>>
  inputGroups?: Readonly<Ref<readonly InputGroup[]>>
  rawParameters: Readonly<Ref<readonly { key: string, label?: string, default?: string }[]>>
  steps: Readonly<Ref<ReadonlyArray<{ config?: unknown } | null | undefined>>>
  batch?: Readonly<Ref<{ role: string, hiddenColumns?: string[] } | null | undefined>>
}

export interface SectionItem {
  /** `${kind}:${inputKey}` — `field:nombre_persona_paga`. The `data-field` anchor. */
  key: string
  kind: 'doc' | 'param' | 'field'
  input?: any
  param?: any
  label: string
  required: boolean
  hint?: string
}

export interface Section { key: string, label: string, description?: string, items: SectionItem[] }

/** An authored `inputGroups` entry — a presentation overlay over FIELD input keys only. */
export interface InputGroup { key: string, label: string, description?: string, inputKeys?: string[] }

/** The bare input key of an item: `field:nombre_persona_paga` → `nombre_persona_paga`. */
export function bareInputKey(itemKey: string): string {
  return itemKey.slice(itemKey.indexOf(':') + 1)
}

// ── Document slots ──────────────────────────────────────────────────────────────────────────────

/** The generic `image` umbrella slot accepts any image file, not a class list. */
export function isImageSlot(input: any): boolean {
  return input?.constraints?.documentType === 'image'
}

/** A `feeds: 'fields'` slot (identity): the step reads EXTRACTED data, never the file, so the
 *  picker gates on the server-derived `has_identity_data` flag instead of format or classifier
 *  bucket (MAR-168). Same axis the entity gate reads, so the two can't disagree. */
export function isFieldsSlot(input: any): boolean {
  return documentTypeById(input?.constraints?.documentType)?.feeds === 'fields'
}

/** Same for the generic `pdf` slot — gated by file extension, since a PDF's contract_type
 *  says nothing about its format (MAR-131). */
export function isPdfSlot(input: any): boolean {
  return input?.constraints?.documentType === 'pdf'
}

export function allowedContractTypesFor(input: any): ContractType[] | undefined {
  // Fields-fed slots gate on data presence alone — a classifier filter here would re-reject
  // eligible documents the gate accepts.
  if (isImageSlot(input) || isPdfSlot(input) || isFieldsSlot(input)) return undefined
  const types = documentTypeById(input?.constraints?.documentType)?.contractTypes
  // Copy into a mutable ContractType[] (source is readonly) for the prop.
  return types ? [...types] : undefined
}

// NOTE (MAR-487, #52): there used to be a `wordEditSlotKeys` / `requireWordFor` pair in the run form,
// deriving the picker's `requireWord` filter from `STEP_SOURCE_MODALITY`. That map is empty, so the
// derivation could only ever answer `false`. The MECHANISM survives in `shared/templating.ts`;
// re-derive the filter here when a step declares a source-modality rule again.

// ── Hints ───────────────────────────────────────────────────────────────────────────────────────

/**
 * Spanish stopwords + the connective noise a generated description carries.
 *
 * Small and closed on purpose: this list decides whether a hint is DELETED, so it must only strip
 * words that cannot themselves be the informative part of a sentence.
 */
const HINT_STOPWORDS = new Set([
  'de', 'del', 'la', 'las', 'el', 'los', 'un', 'una', 'y', 'o', 'en', 'a',
  'numero', 'no',
])

/** Accent- and case-folded content words, for comparing a hint against its own label. */
function contentWords(text: string): Set<string> {
  return new Set(
    text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(w => w && !HINT_STOPWORDS.has(w)),
  )
}

/**
 * Drop a hint that only restates its own label.
 *
 * The template pipeline generates a `description` per field, and on an instrument template most of
 * them echo the label: "Edad trabajador" → *"Edad del trabajador"*. Each costs 22px and a line of grey
 * text the eye must read before discovering it says nothing.
 *
 * SUBSET, never similarity. A hint survives if it contributes even one content word the label does
 * not have, so "Domicilio (ciudad) del trabajador" keeps its "ciudad". Suppressing a hint that
 * carried a real constraint would let a field be filled wrong, so the rule fires only when there is
 * provably nothing to lose. A presentation filter, not an authoring rule — the description still
 * reaches the agent layer and the stored schema untouched.
 */
export function usefulHint(label: string, hint?: string): string | undefined {
  if (!hint) return undefined
  const extra = contentWords(hint)
  for (const word of contentWords(label)) extra.delete(word)
  return extra.size ? hint : undefined
}

// ── Items and sections ──────────────────────────────────────────────────────────────────────────

export function buildDocumentItems(inputs: readonly any[]): SectionItem[] {
  return inputs.filter(i => i?.type === 'document_select').map(input => ({
    key: `doc:${input.key}`, kind: 'doc' as const, input,
    label: input.label || input.key, required: !!input.required,
    hint: usefulHint(input.label || input.key, input.description),
  }))
}

/**
 * Field inputs, then runtime parameters.
 *
 * `template` slots are shown, never filled: the blank .docx resolves server-side at run start from
 * the platform rule (org override ?? Marcus default). They are held out here because they are
 * `required` and would otherwise sit forever incomplete and disable the Run button.
 */
export function buildFieldItems(inputs: readonly any[], params: readonly any[]): SectionItem[] {
  return [
    ...inputs.filter(i => i?.type !== 'document_select' && i?.type !== 'template').map(input => ({
      key: `field:${input.key}`, kind: 'field' as const, input,
      label: input.label || input.key, required: !!input.required,
      hint: usefulHint(input.label || input.key, input.description),
    })),
    ...params.map(param => ({
      key: `param:${param.key}`, kind: 'param' as const, param,
      label: param.label || param.key, required: false,
    })),
  ]
}

export interface SectionLabels { documents: string, documentsHint: string, parameters: string, other: string }

export function buildSections(
  documentItems: SectionItem[],
  fieldItems: SectionItem[],
  groups: readonly InputGroup[],
  labels: SectionLabels,
): Section[] {
  const out: Section[] = []
  if (documentItems.length) {
    out.push({ key: '__documents', label: labels.documents, description: labels.documentsHint, items: documentItems })
  }

  if (!groups.length) {
    // No declared grouping → one section for everything else. Not "advanced", not collapsed:
    // these are ordinary required fields and hiding them behind a chevron was the old bug.
    if (fieldItems.length) out.push({ key: '__fields', label: labels.parameters, items: fieldItems })
    return out
  }

  // `inputGroups` references FIELD input keys only (a presentation overlay over the flat key
  // namespace — see workflowSchemas.ts `inputGroupSchema`).
  const byInputKey = new Map(fieldItems.filter(i => i.kind === 'field').map(i => [i.input.key, i]))
  const claimed = new Set<string>()
  for (const g of groups) {
    const rawItems = (g.inputKeys ?? [])
      .map((k) => { claimed.add(k); return byInputKey.get(k) })
      .filter(Boolean) as SectionItem[]
    const nameItem = rawItems.find(item => item.input?.constraints?.entitySource?.field === 'name')
    const items = nameItem
      ? [nameItem, ...rawItems.filter(item => item !== nameItem)]
      : rawItems
    if (items.length) out.push({ key: g.key, label: g.label, description: g.description, items })
  }
  // Catch-all: any field a group didn't name, plus every runtime parameter (groups never reference
  // parameters). Keeps a stray input visible instead of silently unrendered.
  const leftover = [
    ...fieldItems.filter(i => i.kind === 'field' && !claimed.has(i.input.key)),
    ...fieldItems.filter(i => i.kind === 'param'),
  ]
  if (leftover.length) out.push({ key: '__other', label: labels.other, items: leftover })
  return out
}

/**
 * How many grid columns one field takes: 1 (half a row) or 2 (the whole row).
 *
 * Derived, never authored. `constraints.multiline` is ALREADY the template author's statement that a
 * value is long — `WorkflowFieldControl` reads it to choose a textarea — and this reads the same
 * signal on the horizontal axis. Deliberately NOT a new `constraints.colSpan`: `constraints` is the
 * semantic axis, and layout belongs to the presentation overlay.
 */
export function spanFor(item: SectionItem): 1 | 2 {
  // Document slots, the party chooser and runtime params are controls, not scalars.
  if (item.kind !== 'field') return 2
  const input = item.input
  const constraints = input?.constraints ?? {}
  if (input?.type === 'text' && constraints.multiline) return 2
  // A vertical checkbox list of unbounded height, and a party picker: both full-width by nature.
  if (input?.type === 'multi_select' || input?.type === 'party_select') return 2
  if (typeof constraints.maxLength === 'number' && constraints.maxLength > 120) return 2
  return 1
}

/**
 * What a collapsed field's chip shows: the option's LABEL for a select, the raw value otherwise, so
 * the chip agrees with the field the user sees when they expand it.
 */
export function displayValue(item: SectionItem, inputValues: Record<string, any>): string {
  if (item.kind !== 'field') return ''
  return optionLabelFor(item.input?.constraints?.options, inputValues[item.input.key])
}

// ── Completeness ────────────────────────────────────────────────────────────────────────────────

export function docComplete(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0
}

/**
 * May the run gate count this required input as answered?
 *
 * `!isBlankValue(value)` with three deliberate exceptions (`workflowFieldValues.test.ts` pins them):
 * `false` is not an answer (a required checkbox left unticked); a `party_select` counts once the
 * party has a name; and a `select`/`multi_select` with zero options (AlexAmin/marcus#862 —
 * misconfigured template data) counts as answered, because `WorkflowFieldControl` shows an
 * explanation in place of the dead dropdown and one bad field must not block the whole run.
 */
export function fieldComplete(input: { key?: string, type?: string, constraints?: any }, value: any): boolean {
  if ((input.type === 'select' || input.type === 'multi_select') && !input.constraints?.options?.length) return true
  if (input.type === 'party_select') return !!(value && typeof value === 'object' && String(value.name || '').trim())
  if (value === false) return false
  return !isBlankValue(value)
}

/**
 * Has the chat card's field been answered? The SAME rule as the run form's gate (MAR-948, decided by
 * Alex 2026-09-25): what the card counts as done — its "still missing" list, and the blank count a
 * run is launched with — is exactly what the form would accept. A card field carries its options in
 * `options` as well as `constraints.options`, so both are read, the way the card's own controls do.
 */
export function requestFieldComplete(field: WorkflowRequestField): boolean {
  const options = field.options ?? field.constraints?.options
  return fieldComplete({ key: field.key, type: field.type, constraints: { ...field.constraints, options } }, field.value)
}

export function itemComplete(
  item: SectionItem,
  inputValues: Record<string, any>,
  parameterValues: Record<string, string>,
): boolean {
  if (item.kind === 'doc') return docComplete(inputValues[item.input.key])
  if (item.kind === 'field') return fieldComplete(item.input, inputValues[item.input.key])
  return !!parameterValues[item.param.key]
}

// ── Batch (MAR-179) ─────────────────────────────────────────────────────────────────────────────

/** Is this a field input bound to `role`? The one test behind batched items AND batch columns. */
export function isRoleItem(item: SectionItem, role: string | null | undefined): boolean {
  return !!role && item.kind === 'field' && item.input?.constraints?.entitySource?.role === role
}

/**
 * Every field input bound to the batched role, in form order — the review table's row shape.
 *
 * The server's `batchRowShape` (`shared/templates/batch.ts`) derives the same set from the
 * `TemplateSchema`; this one only ever sees the projected workflow, whose inputs carry the role under
 * `constraints.entitySource.role`. `batchColumnsAgreeWithRowShape.test.ts` pins that the two agree.
 */
export function batchColumnsFor(fieldItems: readonly SectionItem[], role: string | null | undefined): BatchColumn[] {
  if (!role) return []
  return fieldItems.filter(i => isRoleItem(i, role)).map(i => ({
    key: i.input.key,
    label: i.label,
    type: i.input.type,
    required: !!i.required,
    field: i.input.constraints?.entitySource?.field,
    idType: i.input.constraints?.entitySource?.idType,
    options: i.input.constraints?.options,
  }))
}

/** The column a row is keyed on — the one that HOLDS the number, as in `batchRowShape`. Its absence
 *  is what makes batch unavailable. */
export function batchIdentityOf(columns: readonly BatchColumn[]): BatchColumn | null {
  return columns.find(c => isIdentifierProperty(c.field)) ?? null
}

/** A section's field items, in the shape `computePartyBlocks` reads. */
export function partyBlockFields(items: readonly SectionItem[]) {
  return items.filter(i => i.kind === 'field').map(i => ({ key: i.input.key, label: i.label, constraints: i.input?.constraints }))
}
